import React, { useState } from 'react';
import { PendingUser, ApprovedUser } from '@/schemas/admin.types';
import { formatDate } from '@/components/ui/styles/adminHelpers';
import UserAvatar from '../ui/UserAvatar';
import RoleBadge from '../ui/RoleBadge';

interface UserCardProps {
  user: PendingUser | ApprovedUser;
  type: 'pending' | 'approved';
  onApprove?: (user: PendingUser) => Promise<void>;
  onReject?: (user: PendingUser) => Promise<void>;
  onDelete?: (userId: string) => void;
  isDeleting?: boolean;
  videoCount?: number;
}

/**
 * UserCard - Mobile-optimized expandable card component for displaying user information
 * 
 * Provides a compact expandable card layout optimized for mobile viewing.
 * Shows basic info by default, with expandable details and actions.
 */
const UserCard: React.FC<UserCardProps> = ({
  user,
  type,
  onApprove,
  onReject,
  onDelete,
  isDeleting = false,
  videoCount = 0
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTooltip, setShowTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  const handleTextClick = (e: React.MouseEvent, text: string) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Calculate left position - start from left edge of clicked element
    let leftPos = rect.left;
    
    // If tooltip would go off-screen on the right, align it to the right edge
    const tooltipMaxWidth = 320; // approximate max width
    if (leftPos + tooltipMaxWidth > viewportWidth) {
      leftPos = viewportWidth - tooltipMaxWidth - 10; // 10px padding from edge
    }
    
    setShowTooltip({
      text,
      x: leftPos,
      y: rect.bottom + 8
    });
  };


  const handleDeleteClick = () => {
    if ('role' in user && user.role === 'admin') {
      globalThis.alert('Cannot delete admin users - they are protected from deletion');
      return;
    }
    
    if (globalThis.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      onDelete?.(user.id);
    }
  };

  const deleteAction = (() => {
    const isProtected = 'role' in user && user.role === 'admin';

    if (isProtected) {
      return (
        <div className="text-center">
          <span className="text-xs text-gray-400 italic">Protected User</span>
        </div>
      );
    }

    return (
      <button
        onClick={handleDeleteClick}
        disabled={isDeleting}
        type="button"
        className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
      >
        {isDeleting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Deleting...
          </>
        ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Delete
            </>
          )}
      </button>
    );
  })();

  return (
    <>
      {/* Tooltip Popup */}
      {showTooltip && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-transparent border-0 p-0 cursor-default" 
            onClick={() => setShowTooltip(null)}
            onKeyDown={(e) => { if (e.key === 'Escape') setShowTooltip(null); }}
            aria-label="Close tooltip"
          />
          <div 
            className="fixed z-50 bg-gray-800 text-gray-200 text-sm px-4 py-2 rounded-lg shadow-lg max-w-max"
            style={{
              left: `${showTooltip.x}px`,
              top: `${showTooltip.y}px`
            }}
          >
            {showTooltip.text}
          </div>
        </>
      )}

      <div className="w-full max-w-[360px] bg-white border border-gray-200 rounded-[10px] mb-2 overflow-hidden">
      {type === 'pending' ? (
        /* Pending Users: Show info and actions inline */
        <div className="grid grid-cols-3 items-center justify-between gap-4 px-[17px] py-[10px] h-full">
          {/* User Info Section - No Avatar */}
          <div className="flex-1 min-w-0 text-left overflow-hidden">
            <button
              type="button"
              className="text-sm font-medium leading-5 tracking-normal text-gray-900 truncate cursor-pointer hover:text-gray-700 bg-transparent border-0 p-0 text-left w-full" 
              style={{ fontFamily: 'Maven Pro' }}
              onClick={(e) => handleTextClick(e, user.name || 'Unknown User')}
              title={user.name || 'Unknown User'}
              aria-label={`Show full name: ${user.name || 'Unknown User'}`}
            >
              {user.name || 'Unknown User'}
            </button>
            <button
              type="button"
              className="text-sm font-medium leading-5 tracking-normal text-gray-500 truncate cursor-pointer hover:text-gray-600 bg-transparent border-0 p-0 text-left w-full" 
              style={{ fontFamily: 'Maven Pro' }}
              onClick={(e) => handleTextClick(e, user.email)}
              title={user.email}
              aria-label={`Show full email: ${user.email}`}
            >
              {user.email}
            </button>
            <div className="text-xs text-gray-400 mt-1 truncate">
              {formatDate(user.created_at || new Date().toISOString())}
            </div>
            <div className="flex items-center mt-1.5 text-xs text-gray-600">
              <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>{videoCount} {videoCount === 1 ? 'video' : 'videos'}</span>
            </div>
          </div>
          
          {/* Role Badge */}
          <div className="flex items-center justify-center">
            <RoleBadge role={user.role} />
          </div>
          
          {/* Action Buttons - Simple colored circles */}
          <div className="flex items-center justify-center">
            {/* Approve Button - Green circle */}
            <button
              type="button"
              onClick={() => onApprove?.(user as PendingUser)}
              className='bg-transparent p-1'
              title="Approve"
              aria-label="Approve user"
            >
              <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="33" height="33" rx="16.5" stroke="#80DF86"/>
                <path d="M22.3327 13L14.9993 20.3333L11.666 17" stroke="#80DF86" />
              </svg>
            </button>
            
            {/* Reject Button - Red circle */}
            <button
              type="button"
              onClick={() => onReject?.(user as PendingUser)}
              className="bg-transparent p-1"
              title="Reject"
              aria-label="Reject user"
            >
              <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="33" height="33" rx="16.5" stroke="#EE6B6B"/>
                <path d="M12.5 12.5L21.5 21.5" stroke="#EE6B6B" />
                <path d="M21.5 12.5L12.5 21.5" stroke="#EE6B6B" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        /* Approved Users: Expandable card */
        <>
          <button 
            type="button"
            className="flex justify-between w-full bg-transparent items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors gap-4"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse user details' : 'Expand user details'}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <UserAvatar 
                name={user.name} 
                email={user.email} 
                size="md" 
                showGradient={true}
                className="flex-shrink-0"
              />
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm font-medium text-gray-900 truncate text-left">
                  {user.name || 'Unknown User'}
                </div>
                <div className="text-sm text-gray-500 truncate" title={user.email}>
                  {user.email}
                </div>
                <div className="flex items-center mt-1.5 text-xs text-gray-600">
                  <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>{videoCount} {videoCount === 1 ? 'video' : 'videos'}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <RoleBadge role={user.role} />
              {/* Expand/Collapse Arrow */}
              <svg 
                className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          {/* Expandable Content: Additional Details & Actions */}
          {isExpanded && (
            <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
              {/* Additional Info for Approved Users */}
              {'approved_at' in user && (
                <div className="pt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-white p-3 rounded-lg">
                      <div className="text-gray-500 text-xs uppercase tracking-wider mb-1.5 font-semibold">Approved Date</div>
                      <div className="text-gray-900 font-medium">{formatDate(user.approved_at)}</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <div className="text-gray-500 text-xs uppercase tracking-wider mb-1.5 font-semibold">Approved By</div>
                      <div className="text-gray-900 font-medium truncate">{'approved_by' in user ? user.approved_by || 'System' : 'System'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Action for Approved Users */}
              <div className="mt-4">
                {deleteAction}
              </div>
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
};

export default UserCard;
